<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useCreateTrainingStore } from "~/store/trainings/create-training.store";
import { TrainingNavEnum } from "~/utils/enums/training.enums";
import LoadButton from "~/components/Common/Buttons/LoadButton.vue";
import { useAuthStore } from "~/store/auth/auth.store";
import { TagCategoryEnum } from "~/utils/enums/tags.enums";
import { ItemType } from "@simbelapp/order-sdk";
import { useTrainingsStore } from "~/store/trainings/trainings.store";
import AppButton from "~/components/Common/Buttons/AppButton.vue";
import { useTrainingPrefillStore } from "~/store/copilot/training-prefill.store";

defineProps(["currNav"]);
const emit = defineEmits(["update:currNav"]);

const createTrainingStore = useCreateTrainingStore();
const prefillStore = useTrainingPrefillStore();
const { training } = storeToRefs(createTrainingStore);
const load = ref(true);
const authStore = useAuthStore();
const { user } = storeToRefs(authStore);
const router = useRouter();

const { id } = router.currentRoute.value.query as Record<string, string | null>;

async function navigate(nav: TrainingNavEnum) {
  await createTrainingStore.validation();

  if (!Object.keys(training.value.errors).length) emit("update:currNav", nav);
}

function backButton(nav: TrainingNavEnum) {
  emit("update:currNav", nav - 1);
}

async function createTraining() {
  if (load.value) {
    if (user.value?.mainEntityId) {
      await createTrainingStore.addTraining(user.value?.mainEntityId.companyId);
      load.value = false;
    }
  }
}

async function editTraining() {
  load.value = true;

  await createTrainingStore.validation();
  if (!Object.keys(training.value.errors).length) {
    // The store's id covers the AI import (no `?id=`) and a training created in this same session.
    const trainingId = createTrainingStore.trainingId ?? id;
    if (user.value?.mainEntityId && trainingId) {
      await createTrainingStore.assignTagsToItems(
        user.value.mainEntityId.companyId,
        TagCategoryEnum.TRAINING_TAG,
        ItemType.Training,
        training.value.general.trainingTags,
        trainingId,
      );
    }
    const saved = await createTrainingStore.editTraining();
    // The AI review only describes unsaved proposals: once saved, its markers and viewer go.
    if (saved) prefillStore.reset();
  }

  load.value = false;
}
</script>

<template>
  <div class="body_med flex">
    <footer v-if="currNav === TrainingNavEnum.GENERAL" class="training__footer">
      <span
        v-if="!createTrainingStore.isEdit && Object.keys(training.errors).length"
        class="body_regular text-primary-red-color"
      >
        Veuillez remplir tous les champs obligatoires
      </span>
      <AppButton v-if="!createTrainingStore.isEdit" label="Suivant" @click="navigate(TrainingNavEnum.CONTENT)" />
      <div v-else class="flex gap-4 items-center">
        <span v-if="Object.keys(training.errors).length" class="body_regular text-primary-red-color">
          Veuillez remplir tous les champs obligatoires
        </span>
        <LoadButton
          v-model="load"
          class="button__default"
          text-confirm="Enregistrer les modifications"
          @click="editTraining"
        >
        </LoadButton>
      </div>
    </footer>
    <footer v-if="currNav === TrainingNavEnum.CONTENT" class="training__footer">
      <div v-if="!createTrainingStore.isEdit" class="flex w-full justify-between">
        <AppButton type="outlined" label="Retour" @click="backButton(currNav)" />
        <div class="flex items-center gap-5">
          <span v-if="Object.keys(training.errors).length" class="body_regular text-primary-red-color">
            Veuillez remplir tous les champs obligatoires
          </span>
          <AppButton label="Suivant" @click="navigate(TrainingNavEnum.SESSION_ITEM)" />
        </div>
      </div>
      <div v-else class="flex gap-4 items-center">
        <span v-if="Object.keys(training.errors).length" class="body_regular text-primary-red-color">
          Veuillez remplir tous les champs obligatoires
        </span>
        <LoadButton
          v-model="load"
          class="button__default"
          text-confirm="Enregistrer les modifications"
          @click="editTraining"
        >
        </LoadButton>
      </div>
    </footer>

    <footer v-if="currNav === TrainingNavEnum.SESSION_ITEM" class="training__footer">
      <div v-if="!createTrainingStore.isEdit" class="flex w-full justify-between">
        <AppButton type="outlined" label="Retour" @click="backButton(currNav)" />
        <div class="flex items-center gap-5">
          <span v-if="Object.keys(training.errors).length" class="body_regular text-primary-red-color">
            Veuillez remplir tous les champs obligatoires
          </span>
          <AppButton label="Suivant" @click="navigate(TrainingNavEnum.MODALITIES)" />
        </div>
      </div>
      <div v-else class="flex gap-4 items-center">
        <span v-if="Object.keys(training.errors).length" class="body_regular text-primary-red-color">
          Veuillez remplir tous les champs obligatoires
        </span>
        <LoadButton
          v-model="load"
          class="button__default"
          text-confirm="Enregistrer les modifications"
          @click="editTraining"
        >
        </LoadButton>
      </div>
    </footer>

    <footer v-if="currNav === TrainingNavEnum.MODALITIES" class="training__footer">
      <div v-if="!createTrainingStore.isEdit" class="flex w-full justify-between">
        <AppButton type="outlined" label="Retour" @click="backButton(currNav)" />
        <LoadButton v-model="load" text-confirm="Valider et créer la formation" @click="createTraining"></LoadButton>
      </div>
      <div v-else class="flex gap-4 items-center">
        <span v-if="Object.keys(training.errors).length" class="body_regular text-primary-red-color">
          Veuillez remplir tous les champs obligatoires
        </span>
        <LoadButton
          v-model="load"
          class="button__default"
          text-confirm="Enregistrer les modifications"
          @click="editTraining"
        >
        </LoadButton>
      </div>
    </footer>

    <footer v-if="currNav === TrainingNavEnum.SESSIONS && createTrainingStore.isEdit" class="training__footer">
      <LoadButton
        v-model="load"
        :disabled="training.sessions.showAddSession"
        text-confirm="Enregistrer les modifications"
        class="button__default"
        @click="editTraining"
      >
      </LoadButton>
    </footer>
  </div>
</template>

<style scoped lang="scss">
.training__footer {
  @apply pt-[22px] pb-[22px] flex w-full items-center gap-5 justify-end;
}
</style>
